import {
  createHttpApiClient,
  createMockApiClient,
  setSessionHeadersProvider,
  type ApiClient,
} from '@chat-rea/console-ui';
import { vmSessionHeaders } from './auth/session';

/**
 * 讓 console-ui 內的元件（例如「管理 → 工作階段」）也帶得到憑證（問題單 #13）。
 *
 * 元件的 `fetch()` 走 `packages/console-ui/src/api/session-headers.ts`：宿主沒註冊時它只會
 * 退回讀 `chat-rea.pin-token`，所以 **vm-session 登入**（沒有 PIN token）就一條憑證都帶不到、
 * 後端回 401。這裡把宿主本來就有的 `vmSessionHeaders()`（PIN → Bearer、vm-session → x-vm-session）
 * 註冊進去，憑證規則仍然只有一份，不重寫。
 *
 * 註冊放在模組載入時：`client` 也是同一個模組的狀態，任何元件都必須先載入這一支才拿得到 client。
 */
setSessionHeadersProvider(vmSessionHeaders);

/**
 * 後端位址在「執行時」決定，不是 build 時：部署後只要改 /config.json 就能換 API，
 * 不必重建。優先序：
 *   1. fetch('/config.json')（Pages 上的靜態檔，改了立刻生效）
 *   2. window.__CHAT_API__（給臨時注入／除錯用）
 *   3. VITE_API_BASE（build-time，保留給本機 dev 與舊部署）
 *   4. 都沒有 → 記憶體 mock
 * 任何一步失敗都只記錄、不拋錯，最差情況退回 mock，頁面照樣開得起來。
 */

export type RuntimeConfig = {
  apiBase?: string;
  /** `mock` 會強制走記憶體 mock；`live` 或省略則看 apiBase 有沒有值。 */
  mode?: 'live' | 'mock';
};

declare global {
  interface Window {
    __CHAT_API__?: RuntimeConfig;
  }
}

const CONFIG_URL = '/config.json';
const BUILD_API_BASE = (import.meta.env.VITE_API_BASE ?? '').trim();
const CONFIG_TIMEOUT_MS = 4000;

/** 沒有設定時的來源說明，顯示在 header 的 tooltip。 */
export let API_BASE_SOURCE = 'mock';

export let API_BASE = '';
export let IS_MOCK = true;
/**
 * 初始值用「沒有任何情境參數」的 mock：這一行在模組載入時就執行，而那時 `DEV_MOCK_PARAM`
 * 這些常數還沒初始化（用 `createDevMockClient()` 會踩 TDZ）。真正會被畫面用的是
 * `loadApiConfig()` → `applyConfig()` 換掉的 client，所以這裡只是墊檔。
 */
export let client: ApiClient = createMockApiClient();

/**
 * 本機開發／視覺驗收用的 mock 情境（**只有 dev build 生效**，正式部署不受影響）。
 *
 * 真站別開一顆身分要 4–12 秒、也可能直接失敗，但預設 mock 是「0 秒成功」，
 * 所以「開通三階段／超時說明／失敗重試」在預設畫面根本走不出來。用 URL 參數把它做出來：
 *   ?mock-create-ms=9000        建立身分要 9 秒（看得到三段與已耗秒數、超過 5 秒的說明）
 *   ?mock-create-error=1        建立身分一律失敗（`1` 代表用一段含端點與狀態碼的原始錯誤測去技術化）
 *   ?mock-say-error=1           送訊息一律失敗（看「送出失敗／重送」）
 */
const DEV_MOCK_PARAM = {
  createMs: 'mock-create-ms',
  createError: 'mock-create-error',
  sayError: 'mock-say-error',
} as const;

/** 給 `1` 時用一段「像真的後端」的原始錯誤，用來確認畫面不會把端點與狀態碼洩出去。 */
const RAW_ERROR_SAMPLE: Record<'create' | 'say', string> = {
  create: 'POST /api/sessions 500 internal_error',
  say: 'POST /api/events 502 upstream_timeout',
};

function createDevMockClient(): ApiClient {
  if (!import.meta.env.DEV || typeof window === 'undefined') return createMockApiClient();
  const params = new URLSearchParams(window.location.search);
  const delayMs = Number(params.get(DEV_MOCK_PARAM.createMs) ?? '');
  const createError = params.get(DEV_MOCK_PARAM.createError);
  const sayError = params.get(DEV_MOCK_PARAM.sayError);
  return createMockApiClient({
    ...(Number.isFinite(delayMs) && delayMs > 0 ? { createDelayMs: delayMs } : {}),
    ...(createError ? { createError: createError === '1' ? RAW_ERROR_SAMPLE.create : createError } : {}),
    ...(sayError ? { sayError: sayError === '1' ? RAW_ERROR_SAMPLE.say : sayError } : {}),
  });
}

const isRuntimeConfig = (value: unknown): value is RuntimeConfig =>
  typeof value === 'object' && value !== null;

async function readConfigFile(): Promise<RuntimeConfig | null> {
  try {
    const response = await fetch(CONFIG_URL, {
      cache: 'no-cache',
      signal: typeof AbortSignal.timeout === 'function'
        ? AbortSignal.timeout(CONFIG_TIMEOUT_MS)
        : undefined,
    });
    if (!response.ok) return null;
    const data: unknown = await response.json();
    return isRuntimeConfig(data) ? data : null;
  } catch (error) {
    // 檔案不存在、被 SPA fallback 成 index.html、離線、逾時都走這裡：不算錯誤。
    console.debug('[chat-rea] 讀不到', CONFIG_URL, '，改用下一順位設定', error);
    return null;
  }
}

function applyConfig(config: RuntimeConfig | null, source: string): void {
  const apiBase = (config?.apiBase ?? '').trim();
  const forcedMock = config?.mode === 'mock';
  const resolved = forcedMock ? '' : apiBase || BUILD_API_BASE;
  const resolvedSource = forcedMock
    ? 'config.json mode=mock'
    : apiBase
      ? source
      : BUILD_API_BASE
        ? 'VITE_API_BASE'
        : 'mock';

  API_BASE = resolved;
  IS_MOCK = resolved === '';
  API_BASE_SOURCE = resolvedSource;
  client = IS_MOCK ? createDevMockClient() : createHttpApiClient({ baseUrl: resolved, sessionHeaders: vmSessionHeaders });
}

/** 在 render 之前呼叫；永不拋錯。 */
export async function loadApiConfig(): Promise<void> {
  /**
   * 本機 dev：不要被 `public/config.json` 的正式 `apiBase`（workers.dev）帶走——
   * 本機 origin 不在正式 CORS 白名單，會整頁 Failed to fetch。
   * 但 e2e 的 `openWithBrokenBackend` 會攔 config 成 `mode:live` + `http://api.invalid`
   * 來驗「後端掛了」三態，那種非正式 apiBase 要放行。打真 Worker 用 `VITE_API_BASE`。
   */
  if (import.meta.env.DEV && BUILD_API_BASE) {
    applyConfig({ apiBase: BUILD_API_BASE, mode: 'live' }, 'VITE_API_BASE');
    return;
  }
  if (import.meta.env.DEV) {
    const fromFile = await readConfigFile();
    const injectedBase = (fromFile?.apiBase ?? '').trim();
    const wantsLive = fromFile?.mode === 'live' && injectedBase !== '';
    const pointsAtProd = /workers\.dev|chat-rea-proxy\./i.test(injectedBase);
    if (wantsLive && !pointsAtProd) {
      applyConfig(fromFile, CONFIG_URL);
      return;
    }
    applyConfig({ mode: 'mock' }, 'dev-default-mock');
    return;
  }

  const fromFile = await readConfigFile();
  if (fromFile) {
    applyConfig(fromFile, CONFIG_URL);
    return;
  }

  const injected = typeof window === 'undefined' ? undefined : window.__CHAT_API__;
  if (isRuntimeConfig(injected)) {
    applyConfig(injected, 'window.__CHAT_API__');
    return;
  }

  applyConfig(null, 'VITE_API_BASE');
}
